// Sector Data API Integration
// Uses Polygon.io (via Massive API) to fetch sector/industry data for symbols

import type { SectorData } from './summary.types.ts';
import { massiveGet } from '../../api/massiveApi.ts';
import { getCuratedSectorData } from './sectorFallbackData.ts';

const CACHE_KEY = 'dyr_sector_cache_v3';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedSectorData {
  timestamp: number;
  data: Record<string, SectorData>;
}

interface TickerDetailsResponse {
  results: {
    ticker: string;
    name: string;
    market: string;
    locale: string;
    primary_exchange: string;
    type: string;
    active: boolean;
    currency_name: string;
    sic_code: string;
    sic_description: string;
    market_cap?: number;
  };
  status: string;
}

// SIC code to sector mapping (simplified)
const SIC_TO_SECTOR: Record<string, string> = {
  '73': 'Information Technology', // Business Services (includes tech)
  '36': 'Information Technology', // Electronic Equipment
  '35': 'Information Technology', // Industrial Machinery
  '48': 'Communication Services', // Communications
  '27': 'Communication Services', // Printing & Publishing
  '28': 'Health Care',          // Chemicals (includes pharma)
  '38': 'Health Care',          // Instruments (medical)
  '80': 'Health Care',          // Health Services
  '60': 'Financials',           // Banking
  '61': 'Financials',           // Credit Agencies
  '62': 'Financials',           // Securities
  '63': 'Financials',           // Insurance Carriers
  '64': 'Financials',           // Insurance Agents
  '65': 'Financials',           // Real Estate
  '67': 'Financials',           // Holding Companies
  '52': 'Consumer Discretionary', // Building Materials
  '53': 'Consumer Discretionary', // General Merchandise
  '54': 'Consumer Discretionary', // Food Stores
  '55': 'Consumer Discretionary', // Auto Dealers
  '56': 'Consumer Discretionary', // Apparel
  '57': 'Consumer Discretionary', // Furniture
  '58': 'Consumer Discretionary', // Eating/Drinking
  '59': 'Consumer Discretionary', // Miscellaneous Retail
  '20': 'Consumer Staples',     // Food Products
  '21': 'Consumer Staples',     // Tobacco
  '22': 'Consumer Staples',     // Textile Mill
  '23': 'Consumer Staples',     // Apparel Manufacturing
  '29': 'Energy',               // Petroleum Refining
  '13': 'Energy',               // Oil & Gas
  '10': 'Materials',            // Metal Mining
  '12': 'Materials',            // Coal Mining
  '14': 'Materials',            // Mining & Quarrying
  '24': 'Materials',            // Lumber & Wood
  '26': 'Materials',            // Paper & Allied
  '32': 'Materials',            // Stone, Clay, Glass
  '33': 'Materials',            // Primary Metal
  '34': 'Industrials',          // Fabricated Metal
  '37': 'Industrials',          // Transportation Equipment
  '40': 'Industrials',          // Railroad Transportation
  '41': 'Industrials',          // Local Transit
  '42': 'Industrials',          // Motor Freight
  '44': 'Industrials',          // Water Transportation
  '45': 'Industrials',          // Air Transportation
  '47': 'Industrials',          // Transportation Services
  '49': 'Utilities',            // Electric, Gas, Sanitary
  '79': 'Consumer Discretionary', // Amusement & Recreation
  '78': 'Communication Services', // Motion Pictures
};

function mapSicToSector(sicCode: string | undefined, sicDescription: string | undefined): string {
  if (!sicCode) return 'Unknown';

  // Try 2-digit SIC code first
  const twoDigit = sicCode.slice(0, 2);
  if (SIC_TO_SECTOR[twoDigit]) {
    return SIC_TO_SECTOR[twoDigit];
  }

  // Try to infer from description
  const desc = (sicDescription || '').toLowerCase();
  if (desc.includes('software') || desc.includes('computer') || desc.includes('semiconductor')) {
    return 'Information Technology';
  }
  if (desc.includes('pharma') || desc.includes('biotech') || desc.includes('medical')) {
    return 'Health Care';
  }
  if (desc.includes('bank') || desc.includes('insurance') || desc.includes('financial')) {
    return 'Financials';
  }
  if (desc.includes('oil') || desc.includes('gas') || desc.includes('petroleum')) {
    return 'Energy';
  }
  if (desc.includes('retail') || desc.includes('restaurant') || desc.includes('entertainment')) {
    return 'Consumer Discretionary';
  }
  if (desc.includes('food') || desc.includes('beverage') || desc.includes('household')) {
    return 'Consumer Staples';
  }
  if (desc.includes('telecom') || desc.includes('media') || desc.includes('broadcast')) {
    return 'Communication Services';
  }
  if (desc.includes('electric') || desc.includes('utility') || desc.includes('water')) {
    return 'Utilities';
  }
  if (desc.includes('aerospace') || desc.includes('defense') || desc.includes('transport')) {
    return 'Industrials';
  }
  if (desc.includes('chemical') || desc.includes('metal') || desc.includes('mining')) {
    return 'Materials';
  }

  return 'Unknown';
}

function loadCache(): CachedSectorData | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedSectorData = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function saveCache(data: Record<string, SectorData>): void {
  try {
    const cached: CachedSectorData = {
      timestamp: Date.now(),
      data,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage may be full or disabled
  }
}

/**
 * Fetch sector data for a single symbol
 */
async function fetchSectorForSymbol(symbol: string): Promise<SectorData | null> {
  try {
    const response = await massiveGet<TickerDetailsResponse>(
      `/v3/reference/tickers/${encodeURIComponent(symbol)}`
    );

    if (!response.results) return null;

    const { sic_code, sic_description, locale, market_cap } = response.results;
    const sector = mapSicToSector(sic_code, sic_description);

    return {
      symbol,
      sector,
      industry: sic_description || 'Unknown',
      country: locale === 'us' ? 'United States' : locale || 'Unknown',
      marketCap: typeof market_cap === 'number' ? market_cap : undefined,
      source: 'massive-reference-ticker',
    };
  } catch (error) {
    console.warn(`Failed to fetch sector for ${symbol}:`, error);
    return null;
  }
}

/**
 * Get sector data for multiple symbols
 * Uses localStorage cache to minimize API calls
 */
export async function getSectorData(symbols: string[]): Promise<Map<string, SectorData>> {
  const result = new Map<string, SectorData>();

  // Load existing cache
  const cached = loadCache();
  const cachedData = cached?.data || {};

  // Separate cached vs uncached symbols
  const uncached: string[] = [];

  for (const symbol of symbols) {
    // Skip options (they don't have sector data)
    if (symbol.length > 6 || symbol.includes(' ')) continue;

    const upperSymbol = symbol.toUpperCase();
    const curated = getCuratedSectorData(upperSymbol);

    if (curated) {
      result.set(upperSymbol, curated);
    } else if (cachedData[upperSymbol]) {
      result.set(upperSymbol, cachedData[upperSymbol]);
    } else {
      uncached.push(upperSymbol);
    }
  }

  // Fetch uncached symbols (with rate limiting)
  const newData: Record<string, SectorData> = {};

  for (const symbol of uncached) {
    const data = await fetchSectorForSymbol(symbol);
    if (data) {
      result.set(symbol, data);
      newData[symbol] = data;
    } else {
      // Store fallback to avoid re-fetching
      const fallback: SectorData = {
        symbol,
        sector: 'Unknown',
        industry: 'Unknown',
        country: 'Unknown',
      };
      result.set(symbol, fallback);
      newData[symbol] = fallback;
    }

    // Small delay to avoid rate limiting
    if (uncached.indexOf(symbol) < uncached.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // Update cache with new data
  if (Object.keys(newData).length > 0) {
    saveCache({ ...cachedData, ...newData });
  }

  return result;
}

/**
 * Get sector for a single symbol (from cache or fetch)
 */
export async function getSector(symbol: string): Promise<string> {
  const data = await getSectorData([symbol]);
  return data.get(symbol.toUpperCase())?.sector || 'Unknown';
}

/**
 * Clear the sector cache
 */
export function clearSectorCache(): void {
  localStorage.removeItem(CACHE_KEY);
}
